// src/pages/QbusAutomations/BlockPalette.jsx
// Sidebar with draggable block types grouped by category
import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Divider
} from '@mui/material';
import {
  PlayCircle as TriggerIcon,
  CallSplit as ConditionIcon,
  FlashOn as ActionIcon,
  Sensors as OutputIcon,
  NotificationsActive as AlarmIcon,
  ToggleOn as StateIcon,
  AccessTime as TimeIcon,
  CalendarMonth as DayIcon,
  Lightbulb as SetOutputIcon,
  HourglassEmpty as DelayIcon,
  Notes as LogIcon,
  Send as NotifyIcon,
  TheaterComedy as SceneIcon,
  PowerSettingsNew as ActivateIcon,
  Sync as RefreshIcon,
  Warning as TriggerAlarmIcon,
  Http as HttpIcon,
  Api as CaroApiIcon,
  Router as ControllerIcon,
  DataObject as ExtractIcon,
  FindInPage as RegexIcon,
  Rule as IfIcon,
  PlaylistAddCheck as AndIcon,
  AltRoute as OrIcon,
  Code as JsonBuilderIcon,
  Functions as FunctionIcon,
  Bolt as InternalEventIcon,
  AccountTree as FlowControlIcon,
  ForkRight as ForkIcon,
  CallMerge as JoinIcon
} from '@mui/icons-material';

const BLOCK_DEFINITIONS = [
  {
    category: 'trigger',
    label: 'Triggers',
    color: '#ef6c00',
    icon: TriggerIcon,
    blocks: [
      { blockType: 'output_state_changed', label: 'Output State Changed', icon: OutputIcon, description: 'When an output changes state' },
      { blockType: 'api_webhook', label: 'API Webhook', icon: HttpIcon, description: 'Triggered by an incoming API call' },
      { blockType: 'internal_event', label: 'Internal Event', icon: InternalEventIcon, description: 'Triggered by system events (alarms, etc.)' },
    ]
  },
  {
    category: 'condition',
    label: 'Conditions',
    color: '#1976d2',
    icon: ConditionIcon,
    blocks: [
      { blockType: 'if_condition', label: 'If Variable', icon: IfIcon, description: 'Branch on a single comparison' },
      { blockType: 'and_condition', label: 'All True (AND)', icon: AndIcon, description: 'All checks must pass' },
      { blockType: 'or_condition', label: 'Any True (OR)', icon: OrIcon, description: 'Any check can pass' },
      { blockType: 'check_alarm_active', label: 'Check Active Alarm', icon: AlarmIcon, description: 'Is there an active alarm?' },
      { blockType: 'check_output_state', label: 'Check Output State', icon: StateIcon, description: 'Check another output\'s value' },
      { blockType: 'check_controller_online', label: 'Check Controller Online', icon: ControllerIcon, description: 'Is a controller connected?' },
      { blockType: 'check_time', label: 'Check Time', icon: TimeIcon, description: 'Is it within a time window?' },
      { blockType: 'check_day_of_week', label: 'Check Day', icon: DayIcon, description: 'Is today a specific day?' },
    ]
  },
  {
    category: 'flow_control',
    label: 'Flow Control',
    color: '#7b1fa2',
    icon: FlowControlIcon,
    blocks: [
      { blockType: 'parallel', label: 'Parallel (Fork)', icon: ForkIcon, description: 'Execute branches concurrently' },
      { blockType: 'parallel_join', label: 'Join (Wait All)', icon: JoinIcon, description: 'Wait for all branches' },
    ]
  },
  {
    category: 'action',
    label: 'Actions',
    color: '#388e3c',
    icon: ActionIcon,
    blocks: [
      { blockType: 'set_output', label: 'Set Output', icon: SetOutputIcon, description: 'Set a Qbus output value' },
      { blockType: 'set_multiple_outputs', label: 'Set Multiple Outputs', icon: SceneIcon, description: 'Set several outputs at once' },
      { blockType: 'activate_controller', label: 'Activate Controller', icon: ActivateIcon, description: 'Send activate to controller' },
      { blockType: 'refresh_hub_config', label: 'Refresh Hub Config', icon: RefreshIcon, description: 'Force hub config resync' },
      { blockType: 'trigger_alarm', label: 'Trigger Alarm', icon: TriggerAlarmIcon, description: 'Create an alarm record' },
      { blockType: 'http_request', label: 'HTTP Request', icon: HttpIcon, description: 'Call any API endpoint' },
      { blockType: 'caro_api_request', label: 'Caro API Request', icon: CaroApiIcon, description: 'Call CARO API (device auth)' },
      { blockType: 'delay', label: 'Delay', icon: DelayIcon, description: 'Wait before continuing' },
      { blockType: 'log_message', label: 'Log Message', icon: LogIcon, description: 'Log to execution log' },
      { blockType: 'send_notification', label: 'Send Notification', icon: NotifyIcon, description: 'Notify contacts' },
      { blockType: 'extract_params', label: 'Extract Parameters', icon: ExtractIcon, description: 'Extract variables from webhook data' },
      { blockType: 'regex_extract', label: 'Regex Extract', icon: RegexIcon, description: 'Extract parts with regex patterns' },
      { blockType: 'json_builder', label: 'Build JSON', icon: JsonBuilderIcon, description: 'Build JSON body from variables' },
      { blockType: 'function_block', label: 'Function', icon: FunctionIcon, description: 'Run JavaScript on variables' },
    ]
  }
];

const DraggableBlock = ({ block, categoryColor }) => {
  const Icon = block.icon;

  const onDragStart = (event) => {
    event.dataTransfer.setData('application/reactflow-blockType', block.blockType);
    event.dataTransfer.setData('application/reactflow-label', block.label);
    event.dataTransfer.setData('application/reactflow-category', block.category || '');
    event.dataTransfer.effectAllowed = 'move';
  };

  return (
    <Paper
      elevation={1}
      draggable
      onDragStart={onDragStart}
      sx={{
        p: 1,
        mb: 0.75,
        cursor: 'grab',
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        borderLeft: `3px solid ${categoryColor}`,
        '&:hover': { boxShadow: 3, bgcolor: 'action.hover' },
        '&:active': { cursor: 'grabbing' },
        userSelect: 'none',
      }}
    >
      <Icon sx={{ fontSize: 18, color: categoryColor }} />
      <Box>
        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.75rem', lineHeight: 1.2 }}>
          {block.label}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
          {block.description}
        </Typography>
      </Box>
    </Paper>
  );
};

const BlockPalette = () => {
  return (
    <Box sx={{ width: 220, p: 1.5, overflowY: 'auto' }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Blocks
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
        Drag blocks onto the canvas
      </Typography>

      {BLOCK_DEFINITIONS.map((group) => {
        const GroupIcon = group.icon;
        return (
          <Box key={group.category} sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.75 }}>
              <GroupIcon sx={{ fontSize: 14, color: group.color }} />
              <Typography variant="caption" sx={{ fontWeight: 700, color: group.color, textTransform: 'uppercase', fontSize: '0.65rem' }}>
                {group.label}
              </Typography>
            </Box>
            {group.blocks.map((block) => (
              <DraggableBlock
                key={block.blockType}
                block={{ ...block, category: group.category }}
                categoryColor={group.color}
              />
            ))}
            <Divider sx={{ mt: 1 }} />
          </Box>
        );
      })}
    </Box>
  );
};

export { BLOCK_DEFINITIONS };
export default BlockPalette;
